// Client-side messaging permission mirror (Module 17 §7, slice P3-B part 2).
//
// The server is the ONLY authorisation boundary: every §5 route carries one
// code and every cell narrowing (own-identity issue/revoke for every role,
// customer-contact issuance to sales/admins, the planner/finance/qc read-only
// view) is re-checked in the service (D-040 two-layer). This hook exists so
// the screens do not OVER-SHOW controls a role cannot use — a qc lead opening
// the inbox should see the threads, not a composer that 403s on send.
//
// `has()` reads the caller's effective `messaging.*` codes off the permission
// list `GET /v1/auth/tenants` carries (D-586), so a tenant's clone of a seed
// role is offered what it holds. The table below mirrors `systemRoleGrants`
// in packages/contracts and answers only for a feed row without that list.
// `has()` is FAIL-CLOSED — false until the feed settles, false if it fails.
//
// Registered in tools/ci/web-permission-mirror.ts (namespace `messaging.`),
// so a cell here that drifts from the contract table fails the build.

import {
  derivePermissions,
  hasLineage,
  holdsPermission,
  roleSignalOf,
  useRoleCodesFeed,
  type AreaPermissions,
  type RoleInput,
  type RoleSignal,
} from "../../../../lib/role-codes";

/** The twelve `messaging.*` codes after part 2: the ten part-1 codes plus the
 * two conversation codes this slice's routes carry (spec 17 §7; the part-1
 * contracts comment enumerated them so this slice would not re-derive the
 * cells). `messaging.template.submit` is NOT here — it registers at WA
 * unparking with the route that carries it (D-232), absent rather than
 * mirrored as a grant the server would not honour. */
export const MESSAGING_PERMISSIONS = [
  "messaging.account.manage",
  "messaging.settings.read",
  "messaging.link.issue",
  "messaging.identity.manage",
  "messaging.identity.revoke",
  "messaging.template.read",
  "messaging.template.manage",
  "messaging.send",
  "messaging.preferences.manage",
  "messaging.address.read",
  "messaging.conversation.read",
  "messaging.conversation.manage",
] as const;

export type MessagingPermission = (typeof MESSAGING_PERMISSIONS)[number];

/** The three codes EVERY tenant role holds (§7's own-identity cells span the
 * whole matrix: link your own Telegram, revoke your own binding, set your own
 * channel preference — narrowed to "own" service-side for non-admins). */
const EVERY_ROLE_CODES = [
  "messaging.link.issue",
  "messaging.identity.revoke",
  "messaging.preferences.manage",
] as const;

/**
 * Role code → `messaging.*` codes: the messaging slice of the contracts
 * `systemRoleGrants` table (spec 17 §7), transcribed cell by cell.
 *
 * The rows that are easy to get wrong:
 * - group_admin and facility_admin hold ALL TWELVE — §7's settings row
 *   (ga/fa only) is a GRANT difference, but the ga-vs-fa distinctions
 *   elsewhere in the platform are service-side narrowings, never here.
 * - sales holds the full working surface (conversations read+manage,
 *   templates read+manage, identities, send, the address field grant) but
 *   NO settings.read and NO account.manage — §4.4 is the admins' screen.
 * - planner and finance hold conversation.read AND template.read (§4.1
 *   read-only + §4.3 view); qc holds conversation.read ONLY — §4.3's view
 *   row names planner and finance, not qc.
 * - planner/finance/qc do NOT hold `messaging.address.read`: their read-only
 *   views render the MASKED address, which is §7's stated form here (masking,
 *   not omission — unlike the money field grants).
 * - procurement, maintenance, storekeeper and floor_worker hold only the
 *   every-role trio; no conversation codes at all (§7's "—" cells).
 */
export const MESSAGING_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  group_admin: [...MESSAGING_PERMISSIONS],
  facility_admin: [...MESSAGING_PERMISSIONS],
  planner: [...EVERY_ROLE_CODES, "messaging.template.read", "messaging.conversation.read"],
  sales: [
    ...EVERY_ROLE_CODES,
    "messaging.identity.manage",
    "messaging.template.read",
    "messaging.template.manage",
    "messaging.send",
    "messaging.address.read",
    "messaging.conversation.read",
    "messaging.conversation.manage",
  ],
  procurement: [...EVERY_ROLE_CODES],
  finance: [...EVERY_ROLE_CODES, "messaging.template.read", "messaging.conversation.read"],
  qc: [...EVERY_ROLE_CODES, "messaging.conversation.read"],
  maintenance: [...EVERY_ROLE_CODES],
  storekeeper: [...EVERY_ROLE_CODES],
  floor_worker: [...EVERY_ROLE_CODES],
};

/** The effective `messaging.*` set (pure, unit-tested): the feed's
 * permission list cut to this slice, or the mirror without one (D-586). */
export function deriveMessagingPermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, MESSAGING_ROLE_GRANTS);
}

/** §4.5's customer-contact widget audience, mirrored as a ROLE SET (the sales
 * CUSTOMER_SCREEN_ROLES pattern) because it is deliberately NARROWER than the
 * `messaging.link.issue` holder set: every role holds the code for their OWN
 * identity, and the service narrows customer-contact issuance/revocation to
 * exactly these three (D-040). The mirror only decides what is offered. */
export const CONTACT_LINK_ROLES: ReadonlySet<string> = new Set([
  "group_admin",
  "facility_admin",
  "sales",
]);

/** Roles holding `identity.view_members` — the member-directory read the
 * assign dropdown resolves names through. NOT a `messaging.*` code, so it
 * stays out of MESSAGING_ROLE_GRANTS, whose CI mirror is against the
 * messaging slice — the sales ADMIN_ROLES shape, for the same reason. A
 * sales seat assigns without the directory ("Assign to me" — the `"me"`
 * PATCH arm needs no member list and no self-id). */
const ADMIN_ROLES: ReadonlySet<string> = new Set(["group_admin", "facility_admin"]);

export interface MessagingPermissions extends AreaPermissions, RoleSignal {
  readonly roleCodes: ReadonlySet<string>;
  /** True when the role feed itself failed — the screens say so and let the
   * request go, rendering the server's own refusal rather than a blank page. */
  readonly isFeedError: boolean;
  /** §4.1 audience: may open the inbox (read at least). */
  readonly canOpenInbox: boolean;
  /** §4.1 working set: composer, assign/snooze/close/claim, manual retry.
   * For the read-only trio these controls are ABSENT, not disabled. */
  readonly canWorkThreads: boolean;
  /** §4.3 audience: may open the template catalog. */
  readonly canOpenTemplates: boolean;
  /** §4.4 audience: ga/fa — settings, health, webhook log. */
  readonly canOpenSettings: boolean;
  /** §4.4's writes: connect, disconnect, probe and the Email intake card's
   * Turn on, Turn off and Probe (`messaging.account.manage`, D-327 ruling
   * 5). Every system role that opens settings holds it today, so the card's
   * read-only shape (spec 17 §4.4, for `messaging.settings.read` alone) is
   * drawn for a grant set no system role has yet. Its controls are ABSENT
   * there, never disabled. */
  readonly canManageAccounts: boolean;
  /** §4.5 customer-contact widget: may issue/revoke for customer contacts. */
  readonly canLinkContacts: boolean;
  /** The §7 field grant: full addresses; without it the server masks. */
  readonly canReadAddress: boolean;
  /** ga/fa: may read `GET /v1/members` (assign-dropdown names; the server
   * re-checks regardless). */
  readonly isAdmin: boolean;
}

export function useMessagingPermissions(): MessagingPermissions {
  const feed = useRoleCodesFeed();
  const codes = deriveMessagingPermissions(feed);
  return {
    isResolved: feed.isResolved,
    has: (code: string) => codes.has(code),
    ...roleSignalOf(feed),
    isFeedError: feed.isFeedError,
    canOpenInbox: codes.has("messaging.conversation.read"),
    canWorkThreads: codes.has("messaging.conversation.manage"),
    canOpenTemplates: codes.has("messaging.template.read"),
    canOpenSettings: codes.has("messaging.settings.read"),
    canManageAccounts: codes.has("messaging.account.manage"),
    // `identities.service.ts` narrows customer-contact issuance on lineage.
    canLinkContacts: codes.has("messaging.link.issue") && hasLineage(feed, CONTACT_LINK_ROLES),
    canReadAddress: codes.has("messaging.address.read"),
    // The member directory the assign dropdown reads (`identity.view_members`).
    isAdmin: holdsPermission(feed, "identity.view_members", ADMIN_ROLES),
  };
}

// ---------------------------------------------------------------------------
// THE AREA'S ENTRY TABLE — one rule, read by the app nav AND the sub-nav
// (the waste-layout doctrine: two gating rules for one area is the defect).
// ---------------------------------------------------------------------------

export interface MessagingAreaEntry {
  /** The route path. */
  readonly to: string;
  /** The dictionary key for its label (`messaging.nav.*`). */
  readonly labelKey: string;
  /** Stable walkthrough handle for the sub-nav link (the part-2 testid
   * contract: `nav-messaging-settings` is how M17-S01/S12 reach §4.4). */
  readonly testid: string;
}

/** The permission facts the entry table reads — the shape both callers hold. */
export interface MessagingEntryPermissions {
  readonly canOpenInbox: boolean;
  readonly canOpenTemplates: boolean;
  readonly canOpenSettings: boolean;
}

/**
 * The messaging screens this principal may open, in landing order (pure).
 *
 * ORDER IS THE LANDING ORDER, and it is deliberate: the inbox first (the
 * screen that answers "who wrote to us" without an id in hand), the template
 * catalog second, and settings last — the ga/fa-only configuration surface
 * (§4.4: everyone else gets NO entry, not a link into a 403 — D-088).
 */
export function messagingAreaEntries(perms: MessagingEntryPermissions): MessagingAreaEntry[] {
  const entries: MessagingAreaEntry[] = [];
  if (perms.canOpenInbox) {
    entries.push({
      to: "/messaging/inbox",
      labelKey: "messaging.nav.inbox",
      testid: "nav-messaging-inbox",
    });
  }
  if (perms.canOpenTemplates) {
    entries.push({
      to: "/messaging/templates",
      labelKey: "messaging.nav.templates",
      testid: "nav-messaging-templates",
    });
  }
  if (perms.canOpenSettings) {
    entries.push({
      to: "/messaging/settings",
      labelKey: "messaging.nav.settings",
      testid: "nav-messaging-settings",
    });
  }
  return entries;
}

/** Where `/messaging` (and the app-nav link) lands this principal, or
 * `undefined` when the area holds nothing for them. */
export function messagingLandingPath(perms: MessagingEntryPermissions): string | undefined {
  return messagingAreaEntries(perms)[0]?.to;
}

/**
 * Is this principal REFUSED a messaging screen — a stable fact, not a failure?
 *
 * The sales `isSalesReadRefused` contract verbatim: true only once the role
 * feed has settled AND did not fail; while it is in flight, or when it FAILED,
 * the answer is "let the request go and render the server's own refusal" —
 * the fail-open direction, because the mirror is a display aid and the server
 * is the authorisation boundary (D-040's two layers).
 */
export function isMessagingReadRefused(
  perms: Pick<MessagingPermissions, "isResolved" | "isFeedError">,
  allowed: boolean,
): boolean {
  return perms.isResolved && !perms.isFeedError && !allowed;
}
